import { ESdChangeReason, IConditionModel, IVisibility } from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaCollapsePanel from "../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import { Comparison } from "./condition";
import PropertyRowIcon from "./property-row-icon";

type VisibilityType = NonNullable<IVisibility["visibilityType"]>;

const DEFAULT_VISIBILITY_TYPE: VisibilityType = "visible";

export interface IVisibilityPanelState {
  isVisibilityPanelActive: boolean;
}

export interface IVisibilityPanelProps {
  onVisibilitySaved: (validation: IVisibility, changeReason?: ESdChangeReason | undefined) => void;
  visibility: IVisibility;
}

class VisibilityPanel extends PureSdComponent<IVisibilityPanelProps & IWithSDProps, IVisibilityPanelState> {
  constructor(props: IVisibilityPanelProps & IWithSDProps) {
    super(props);
    this.state = {
      isVisibilityPanelActive: false
    };
  }

  // There is no fourth state: the model's type is a non-nullable enum defaulting to "visible", and
  // the select already reads a missing type as "Always Visible". Clearing resets to that default
  // rather than storing a null the select would still show as "Always Visible".
  handleStatusChange = (value: VisibilityType | null): void => {
    const visibility = _.cloneDeep(this.props.visibility);
    visibility.visibilityType = value ?? DEFAULT_VISIBILITY_TYPE;

    if (visibility.visibilityType !== "custom") {
      visibility.condition = undefined;
    }

    this.props.onVisibilitySaved(visibility);
  };

  handleActionCondition = (condition: IConditionModel, changeReason?: ESdChangeReason | undefined) => {
    const visibility = _.cloneDeep(this.props.visibility);
    visibility.condition = condition;
    this.props.onVisibilitySaved(visibility, changeReason);
  };

  render() {
    return (
      <>
        <KuikaCollapsePanel
          activeKey={this.state.isVisibilityPanelActive ? this.props.visibility.id : ""}
          onChange={() => {
            this.setState({ isVisibilityPanelActive: !this.state.isVisibilityPanelActive });
          }}
          iconPlacement="right"
          className="kuika-right-panel__collapse kuika-right-panel__collapse-lighter kuika-right-panel__collapse--boxed kuika-right-panel__collapse--visibility"
          items={[
            {
              key: this.props.visibility.id,
              label: (
                <>
                  <PropertyRowIcon kind="visibility" />
                  <KuikaTextMedium data-onboarding-id="visibility-panel-header-title">Visibility</KuikaTextMedium>
                </>
              ),
              children: (
                <div className="kuika-right-panel__collapse-body">
                  <div className="kuika-right-panel__col kuika_property-grid-column kuika_select_property">
                    <div className="kuika-right-panel__row">
                      <div className="kuika-right-panel__col">
                        <KuikaSmallText>Will be ...</KuikaSmallText>
                        <KuikaSelectbox
                          allowClear
                          value={this.props.visibility.visibilityType || DEFAULT_VISIBILITY_TYPE}
                          placeholder="Select"
                          onChange={this.handleStatusChange}
                          options={[
                            { value: "visible", label: "Always Visible" },
                            { value: "hidden", label: "Hidden" },
                            { value: "custom", label: "Sometimes Visible" }
                          ]}
                        />
                      </div>
                    </div>

                    {this.props.visibility.visibilityType === "custom" && (
                      <div className="kuika-right-panel__row">
                        <div className="kuika-right-panel__col">
                          <Comparison
                            condition={this.props.visibility.condition}
                            onChange={this.handleActionCondition}
                            conditionType="visibility"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            }
          ]}
        />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateVisibilty ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    ) {
      return true;
    }
    return false;
  };
}

const visibilityPanel = withSD<IVisibilityPanelProps>(VisibilityPanel);
export { visibilityPanel as VisibilityPanel };
